import React from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Check, Minus, Plus, ShoppingBag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ApparelProduct } from "@/types";

interface ProductControlsProps {
  product: ApparelProduct;
  quantity: number;
  selectedSize: string;
  addedToCart: boolean;
  onQuantityChange: (quantity: number) => void;
  onSizeSelect: (size: string) => void;
  onAddToCart: () => void;
}

export const ProductControls: React.FC<ProductControlsProps> = ({
  product,
  quantity,
  selectedSize,
  addedToCart,
  onQuantityChange,
  onSizeSelect,
  onAddToCart,
}) => {
  const sizes = ["XS", "S", "M", "L", "XL", "XXL"];
  const isOutOfStock = product.has_inventory && 
    product.inventory_count !== null && 
    product.inventory_count <= 0;

  return (
    <div className="space-y-6">
      {/* Size Selection */}
      {product.requires_size && (
        <div className="space-y-3">
          <p className="text-sm font-medium">Size</p>
          <div className="flex flex-wrap gap-2">
            {sizes.map(size => (
              <Button
                key={size}
                type="button"
                variant={selectedSize === size ? "default" : "outline"}
                size="sm"
                className={`w-11 h-11 rounded-lg p-0 ${
                  selectedSize === size
                    ? 'bg-hivetivity-primary text-white hover:bg-hivetivity-primary/90'
                    : 'hover:bg-muted/20 border-muted/50'
                }`}
                onClick={() => onSizeSelect(size)}
              >
                {size}
              </Button>
            ))}
          </div>
          {product.requires_size && !selectedSize && (
            <p className="text-xs text-muted-foreground">Please select a size</p>
          )}
        </div>
      )}

      {/* Quantity Selection */}
      <div className="space-y-3">
        <p className="text-sm font-medium">Quantity</p>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-lg p-0 border-muted/50"
            onClick={() => onQuantityChange(quantity - 1)}
            disabled={quantity <= 1 || isOutOfStock}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <span className="w-8 text-center">{quantity}</span>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-lg p-0 border-muted/50"
            onClick={() => onQuantityChange(quantity + 1)}
            disabled={
              isOutOfStock ||
              (product.has_inventory &&
                product.inventory_count !== null &&
                quantity >= product.inventory_count)
            }
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Separator className="my-6" />

      {/* Add to Cart Button */}
      <Button
        className="w-full h-12 relative overflow-hidden group"
        onClick={onAddToCart}
        disabled={isOutOfStock || (product.requires_size && !selectedSize)}
      >
        <AnimatePresence mode="wait">
          {addedToCart ? (
            <motion.div
              key="added"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex items-center"
            >
              <Check className="h-5 w-5 mr-2" />
              Added to Cart
            </motion.div>
          ) : (
            <motion.div
              key="add"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="flex items-center"
            >
              <ShoppingBag className="h-5 w-5 mr-2" />
              {isOutOfStock
                ? "Out of Stock"
                : product.requires_size && !selectedSize
                ? "Select a Size"
                : "Add to Cart"}
            </motion.div>
          )}
        </AnimatePresence>
      </Button>
    </div>
  );
};
